import type { Comment, CommentPosition } from '../bffComments';

export interface PinPoint { x: number; y: number }

const clamp = (value: number, max = 1) => Math.max(0, Math.min(max, value));
const quote = (value: string) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\a ')}"`;

function segment(element: Element): string {
  const tag = element.tagName.toLowerCase();
  for (const attr of ['data-comment-anchor', 'data-comment-view', 'id', 'name', 'aria-label']) {
    const value = element.getAttribute(attr);
    if (value) return `${tag}[${attr}=${quote(value)}]`;
  }
  const siblings = Array.from(element.parentElement?.children ?? []).filter((e) => e.tagName === element.tagName);
  return `${tag}:nth-of-type(${siblings.indexOf(element) + 1})`;
}

export function capturePosition(root: HTMLElement, x: number, y: number): CommentPosition {
  const rect = root.getBoundingClientRect();
  const hit = document.elementsFromPoint(x, y).find((element) =>
    root.contains(element) && !element.closest('[data-comment-ui]'));
  if (!hit || hit === root) throw new Error('Choisis un composant du prototype.');
  const target = hit.closest('button, a, input, textarea, select, td, th, label, h1, h2, h3, canvas') ?? hit.closest('[data-comment-anchor]') ?? hit;
  const parts: string[] = [];
  let node: Element | null = target;
  while (node && node !== root) {
    parts.unshift(segment(node));
    node = node.parentElement;
  }
  if (node !== root) throw new Error('Ce composant est hors du prototype.');
  const bounds = target.getBoundingClientRect();
  const stable = target.closest('[data-comment-anchor]');
  const views: string[] = [];
  for (let ancestor = stable?.parentElement; ancestor && ancestor !== root; ancestor = ancestor.parentElement) {
    if (ancestor.hasAttribute('data-comment-view')) views.unshift(segment(ancestor));
  }
  let selector = `:scope > ${parts.join(' > ')}`;
  if (stable && root.contains(stable)) {
    const tail: string[] = [];
    for (let child: Element | null = target; child && child !== stable; child = child.parentElement) tail.unshift(segment(child));
    selector = `:scope ${[...views, segment(stable)].join(' ')}${tail.length ? ` > ${tail.join(' > ')}` : ''}`;
  }
  if (selector.length > 2048) throw new Error('Ce composant nécessite un identifiant data-comment-anchor plus court.');
  return {
    xPct: clamp((x - rect.left) / rect.width) * 100,
    yPct: clamp((y - rect.top) / rect.height) * 100,
    anchor: {
      selector,
      xRatio: clamp((x - bounds.left) / bounds.width),
      yRatio: clamp((y - bounds.top) / bounds.height),
    },
  };
}

export function resolvePosition(root: HTMLElement, comment: Pick<Comment, 'anchor' | 'xPct' | 'yPct'>): PinPoint | null {
  let target: Element = root;
  if (comment.anchor) {
    try {
      const matches = root.querySelectorAll(comment.anchor.selector);
      if (matches.length !== 1 || matches[0].closest('[data-comment-ui]')) return null;
      target = matches[0];
    } catch { return null; } // Old/invalid selectors remain accessible from the comments list.
  }
  const rect = target.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  const x = rect.left + rect.width * (comment.anchor?.xRatio ?? (comment.xPct ?? 0) / 100);
  const y = rect.top + rect.height * (comment.anchor?.yRatio ?? (comment.yPct ?? 0) / 100);
  if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return null;
  for (let node: Element | null = target; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return null;
    const bounds = node.getBoundingClientRect();
    if (/(auto|scroll|hidden|clip)/.test(style.overflowX) && (x < bounds.left || x > bounds.right)) return null;
    if (/(auto|scroll|hidden|clip)/.test(style.overflowY) && (y < bounds.top || y > bounds.bottom)) return null;
  }
  return { x, y };
}
